import { useState, useEffect, useRef } from 'react'

/**
 * useDebounce
 * Adds a delay between the user entering and value and the value being processed,
 * each time the value is changes the processing is delayed. This helps prevent
 * BE requests for every key when a user is typing.
 * @param value - value being set with the delay
 * @param delay - delay between value being set and processed (assuming no additional
 *   input), either a fixed number or a function of the value returning the delay to use
 *   (e.g. a shorter delay once enough characters have been entered)
 * @returns value
 */
export default function useDebounce<T>(
  value: T,
  delay: number | ((value: T) => number)
): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  const delayRef = useRef(delay)
  delayRef.current = delay

  useEffect(() => {
    const resolvedDelay =
      typeof delayRef.current === 'function'
        ? delayRef.current(value)
        : delayRef.current

    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, resolvedDelay)

    return () => clearTimeout(handler)
    // delay is intentionally excluded — reading it via delayRef avoids
    // restarting the timer when only the delay function's reference changes,
    // which happens on every render if the caller passes an inline function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return debouncedValue
}
